import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Edit2,
  ExternalLink,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

import ModalShell from './ModalShell';
import { API_URL } from '../config';

const clientAppRequest = async (path, options = {}) => {
  const response = await fetch(`${API_URL}/api/client-catalog-admin/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(options.body && !(options.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    throw new Error(
      typeof detail === 'string'
        ? detail
        : detail?.message || 'Не удалось выполнить действие'
    );
  }
  return payload;
};

const TONES = [
  { id: 'blue', label: 'Синий', bg: 'from-blue-600 to-indigo-700', border: 'border-blue-500' },
  { id: 'emerald', label: 'Изумрудный', bg: 'from-emerald-600 to-teal-700', border: 'border-emerald-500' },
  { id: 'amber', label: 'Янтарный', bg: 'from-amber-500 to-orange-600', border: 'border-amber-500' },
  { id: 'rose', label: 'Розовый', bg: 'from-rose-500 to-pink-600', border: 'border-rose-500' },
  { id: 'violet', label: 'Фиолетовый', bg: 'from-violet-600 to-purple-800', border: 'border-violet-500' },
  { id: 'slate', label: 'Тёмный', bg: 'from-slate-800 to-gray-950', border: 'border-slate-500' },
];

const getToneGradient = (tone) => {
  const found = TONES.find((t) => t.id === tone);
  return found ? found.bg : 'from-blue-600 to-indigo-700';
};

const ACTION_TYPES = [
  { id: 'catalog', label: 'Каталог услуг' },
  { id: 'service', label: 'Конкретная услуга' },
  { id: 'cart', label: 'Корзина заказа' },
  { id: 'orders', label: 'Мои заказы' },
  { id: 'link', label: 'Внешняя/внутренняя ссылка' },
];

const DEFAULT_FORM = {
  title: '',
  subtitle: '',
  badge: 'Акция',
  image_url: null,
  tone: 'blue',
  action_type: 'catalog',
  action_payload: '',
  cta_text: 'Подробнее',
  is_active: true,
  sort_order: 0,
};

const ClientBannerManager = ({ services = [] }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const loadBanners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await clientAppRequest('/banners');
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки баннеров');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const openCreateModal = () => {
    setEditingBanner(null);
    setFormData(DEFAULT_FORM);
    setImageFile(null);
    setImagePreview('');
    setError('');
    setNotice('');
    setModalOpen(true);
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      badge: banner.badge || '',
      image_url: banner.image_url || null,
      tone: banner.tone || 'blue',
      action_type: banner.action_type || 'catalog',
      action_payload: banner.action_payload || '',
      cta_text: banner.cta_text || 'Подробнее',
      is_active: Boolean(banner.is_active),
      sort_order: banner.sort_order || 0,
    });
    setImageFile(null);
    setImagePreview(banner.image_url ? `${API_URL}${banner.image_url}` : '');
    setError('');
    setNotice('');
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData((prev) => ({ ...prev, image_url: null }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Укажите заголовок баннера');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      let savedBanner;
      const payload = {
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim() || null,
        badge: formData.badge.trim() || null,
        image_url: formData.image_url,
        tone: formData.tone || 'blue',
        action_type: formData.action_type || 'catalog',
        action_payload: formData.action_payload.trim() || null,
        cta_text: formData.cta_text.trim() || 'Подробнее',
        is_active: Boolean(formData.is_active),
        sort_order: Number(formData.sort_order || 0),
      };

      if (editingBanner) {
        savedBanner = await clientAppRequest(`/banners/${editingBanner.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        savedBanner = await clientAppRequest('/banners', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      // If an image file was selected, upload it
      if (imageFile && savedBanner?.id) {
        const uploadData = new FormData();
        uploadData.append('file', imageFile);
        await clientAppRequest(`/banners/${savedBanner.id}/image`, {
          method: 'POST',
          body: uploadData,
        });
      }

      setNotice(
        editingBanner ? 'Баннер успешно обновлён' : 'Баннер успешно создан'
      );
      setModalOpen(false);
      await loadBanners();
    } catch (err) {
      setError(err.message || 'Ошибка сохранения баннера');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await clientAppRequest(`/banners/${banner.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...banner,
          is_active: !banner.is_active,
        }),
      });
      setBanners((prev) =>
        prev.map((b) =>
          b.id === banner.id ? { ...b, is_active: !b.is_active } : b
        )
      );
    } catch (err) {
      setError(err.message || 'Не удалось обновить статус баннера');
    }
  };

  const handleDelete = async (id) => {
    try {
      await clientAppRequest(`/banners/${id}`, {
        method: 'DELETE',
      });
      setDeleteConfirmId(null);
      setBanners((prev) => prev.filter((b) => b.id !== id));
      setNotice('Баннер удалён');
    } catch (err) {
      setError(err.message || 'Не удалось удалить баннер');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Промо-баннеры клиентского приложения
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Отображаются в виде карусели на главной странице клиента. Привлекают
            внимание к акциям, новинкам и сезонным услугам.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadBanners}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
            Обновить
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition"
          >
            <Plus className="h-4 w-4" />
            Добавить баннер
          </button>
        </div>
      </div>

      {/* Status messages */}
      {(error || notice) && (
        <div
          className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {error ? (
              <AlertCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            )}
            <span>{error || notice}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setError('');
              setNotice('');
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Banner list */}
      {loading ? (
        <div className="flex items-center justify-center rounded-2xl bg-white p-12 text-gray-500 shadow-sm dark:bg-gray-900">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Загрузка баннеров…
        </div>
      ) : banners.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-10 text-center dark:border-gray-800 dark:bg-gray-900">
          <Sparkles className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">
            Нет активных баннеров
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Создайте первый баннер, чтобы клиенты увидели акции на главной
            странице.
          </p>
          <button
            type="button"
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition"
          >
            <Plus className="h-4 w-4" />
            Создать баннер
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {banners.map((banner) => {
            const gradient = getToneGradient(banner.tone);
            const imageSrc = banner.image_url ? `${API_URL}${banner.image_url}` : '';

            return (
              <div
                key={banner.id}
                className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
              >
                {/* Banner preview card */}
                <div
                  className={`relative min-h-[160px] p-5 bg-gradient-to-br ${gradient} text-white flex flex-col justify-between overflow-hidden`}
                >
                  {imageSrc && (
                    <img
                      src={imageSrc}
                      alt={banner.title}
                      className="absolute right-0 bottom-0 h-full w-2/5 object-cover opacity-20 pointer-events-none rounded-l-2xl mix-blend-overlay"
                    />
                  )}

                  <div className="relative z-10 flex items-start justify-between gap-2">
                    {banner.badge ? (
                      <span className="inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold tracking-wide uppercase backdrop-blur-sm">
                        {banner.badge}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium backdrop-blur-sm ${
                        banner.is_active
                          ? 'bg-emerald-500/30 text-emerald-100 border border-emerald-400/30'
                          : 'bg-black/30 text-gray-300 border border-white/10'
                      }`}
                    >
                      {banner.is_active ? 'Активен' : 'Отключён'}
                    </span>
                  </div>

                  <div className="relative z-10 my-3">
                    <h4 className="text-base font-bold leading-tight drop-shadow-sm">
                      {banner.title}
                    </h4>
                    {banner.subtitle && (
                      <p className="mt-1 text-xs text-white/85 line-clamp-2">
                        {banner.subtitle}
                      </p>
                    )}
                  </div>

                  <div className="relative z-10 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 rounded-xl bg-white/25 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm">
                      {banner.cta_text || 'Подробнее'}
                      <ExternalLink className="h-3 w-3" />
                    </span>
                    <span className="text-[11px] text-white/70">
                      Порядок: {banner.sort_order}
                    </span>
                  </div>
                </div>

                {/* Banner admin footer controls */}
                <div className="flex items-center justify-between border-t border-gray-100 p-3 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-850">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(banner)}
                      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                        banner.is_active
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300'
                      }`}
                      title={
                        banner.is_active
                          ? 'Скрыть из приложения'
                          : 'Опубликовать в приложении'
                      }
                    >
                      {banner.is_active ? (
                        <>
                          <EyeOff className="h-3 w-3" />
                          Скрыть
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3" />
                          Показать
                        </>
                      )}
                    </button>
                    <span className="text-[11px] text-gray-400 capitalize">
                      {ACTION_TYPES.find((a) => a.id === banner.action_type)
                        ?.label || banner.action_type}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => openEditModal(banner)}
                      className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:text-gray-400 dark:hover:bg-gray-800"
                      title="Редактировать"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    {deleteConfirmId === banner.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleDelete(banner.id)}
                          className="rounded-lg bg-red-600 px-2 py-1 text-[11px] font-bold text-white hover:bg-red-700"
                        >
                          Да
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(null)}
                          className="rounded-lg border px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-100"
                        >
                          Нет
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(banner.id)}
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-800"
                        title="Удалить"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <ModalShell
          title={editingBanner ? 'Редактировать баннер' : 'Новый баннер акции'}
          onClose={() => setModalOpen(false)}
          maxWidth="max-w-3xl"
        >
          <form onSubmit={handleSave} className="space-y-4">
            {/* Live Phone Preview in Modal */}
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                Предпросмотр баннера в приложении
              </label>
              <div
                className={`relative min-h-[140px] rounded-2xl p-5 bg-gradient-to-br ${getToneGradient(
                  formData.tone
                )} text-white flex flex-col justify-between overflow-hidden shadow-sm`}
              >
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Предпросмотр"
                    className="absolute right-0 bottom-0 h-full w-2/5 object-cover opacity-25 pointer-events-none rounded-l-2xl mix-blend-overlay"
                  />
                )}
                <div className="relative z-10 flex items-start justify-between">
                  {formData.badge ? (
                    <span className="inline-block rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold tracking-wide uppercase backdrop-blur-sm">
                      {formData.badge}
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className="text-[10px] text-white/70">
                    {formData.is_active ? 'Будет виден' : 'Будет скрыт'}
                  </span>
                </div>
                <div className="relative z-10 my-2">
                  <h4 className="text-base font-bold leading-tight">
                    {formData.title || 'Заголовок промо-акции'}
                  </h4>
                  {formData.subtitle && (
                    <p className="mt-1 text-xs text-white/85">
                      {formData.subtitle}
                    </p>
                  )}
                </div>
                <div className="relative z-10">
                  <span className="inline-flex items-center gap-1 rounded-xl bg-white/25 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm">
                    {formData.cta_text || 'Подробнее'}
                    <ExternalLink className="h-3 w-3" />
                  </span>
                </div>
              </div>
            </div>

            {/* Form inputs */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Заголовок акции *
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="Например: Скидка 20% на стирку пледов"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Подзаголовок / условия
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) =>
                      setFormData({ ...formData, subtitle: e.target.value })
                    }
                    placeholder="Например: Только до конца месяца при заказе от 2000 ₽"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Бейдж (метка)
                  <input
                    type="text"
                    value={formData.badge}
                    onChange={(e) =>
                      setFormData({ ...formData, badge: e.target.value })
                    }
                    placeholder="АКЦИЯ, ХИТ, СЕЗОН, СКИДКА"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Текст кнопки действия (CTA)
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) =>
                      setFormData({ ...formData, cta_text: e.target.value })
                    }
                    placeholder="Подробнее, Заказать, Выбрать"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>

              {/* Tone color selector */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Цветовая гамма фона
                </label>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {TONES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setFormData({ ...formData, tone: t.id })}
                      className={`flex items-center gap-2 rounded-xl border p-2 text-left transition ${
                        formData.tone === t.id
                          ? `${t.border} bg-blue-50 font-bold dark:bg-blue-950/30`
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span
                        className={`h-4 w-4 rounded-full bg-gradient-to-br ${t.bg} shrink-0`}
                      />
                      <span className="text-xs truncate">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action type & payload */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Действие при нажатии
                  <select
                    value={formData.action_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        action_type: e.target.value,
                        action_payload: '',
                      })
                    }
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {ACTION_TYPES.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div>
                {formData.action_type === 'service' ? (
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Выберите услугу
                    <select
                      value={formData.action_payload}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          action_payload: e.target.value,
                        })
                      }
                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">-- Выберите услугу --</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : formData.action_type === 'link' ? (
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Ссылка перехода
                    <input
                      type="text"
                      value={formData.action_payload}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          action_payload: e.target.value,
                        })
                      }
                      placeholder="https://... или /catalog"
                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                  </label>
                ) : (
                  <div className="pt-6 text-xs text-gray-400">
                    Параметры не требуются для этого действия
                  </div>
                )}
              </div>

              {/* Image upload */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Фоновое изображение баннера (опционально)
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                    <Upload className="h-4 w-4" />
                    Загрузить изображение
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="inline-flex items-center gap-1 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-100 dark:bg-red-950/30"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Удалить фото
                    </button>
                  )}
                </div>
              </div>

              {/* Status and sort order */}
              <div className="flex items-center gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                  />
                  Отображать баннер на главной
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Порядок сортировки
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sort_order: Number(e.target.value),
                      })
                    }
                    className="mt-1 w-24 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {editingBanner ? 'Сохранить изменения' : 'Создать баннер'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

export default ClientBannerManager;
